import sodium from 'libsodium-wrappers-sumo';
import { fromB64, sodiumReady, toB64 } from './vault.ts';

/**
 * Der Schlüssel des versiegelten Eingangs (docs/KONZEPT.md, Abschnitt 20).
 *
 * Ein Einlieferer soll etwas hinterlegen können, ohne irgendetwas entschlüsseln zu
 * können — auch nicht das, was er selbst abgelegt hat. Genau dafür ist ein Sealed
 * Box gebaut: Der Absender kennt nur den öffentlichen Schlüssel, erzeugt für jede
 * Sendung ein Wegwerf-Paar und vernichtet dessen privaten Teil. Öffnen kann nur, wer
 * den privaten Schlüssel hat — und der liegt im Vault, also niemals auf dem Server.
 *
 * Der öffentliche Schlüssel darf offen herumliegen; er ist keine Berechtigung.
 * Wer einliefern darf, entscheidet der Einlieferungsschlüssel, nicht dieser hier.
 */

export type EingangSchluessel = {
  /** base64, 32 Byte — geht an den Server und an den Einlieferer. */
  publicKey: string;
  /** base64, 32 Byte — bleibt im Vault. */
  secretKey: string;
};

export async function neuerEingangSchluessel(): Promise<EingangSchluessel> {
  await sodiumReady();
  const paar = sodium.crypto_box_keypair();
  return { publicKey: toB64(paar.publicKey), secretKey: toB64(paar.privateKey) };
}

/**
 * Öffnet einen Umschlag. Schlägt fehl, wenn er für einen anderen Schlüssel versiegelt
 * wurde — das ist kein Randfall, sondern der normale Zustand nach einem Schlüssel-
 * wechsel, und die Meldung muss das sagen können.
 */
export function oeffneUmschlag(sealed: string, schluessel: EingangSchluessel): Uint8Array {
  return sodium.crypto_box_seal_open(
    fromB64(sealed),
    fromB64(schluessel.publicKey),
    fromB64(schluessel.secretKey),
  );
}

/**
 * Dasselbe für rohe Bytes. Eine eingelieferte Datei kann hundert Megabyte wiegen —
 * die erst nach base64 und zurück zu wandeln, kostet Zeit und den dreifachen Speicher.
 */
export function oeffneUmschlagRoh(sealed: Uint8Array, schluessel: EingangSchluessel): Uint8Array {
  return sodium.crypto_box_seal_open(
    sealed,
    fromB64(schluessel.publicKey),
    fromB64(schluessel.secretKey),
  );
}

/**
 * Versiegeln für einen fremden öffentlichen Schlüssel — dieselbe Sealed Box, nur in
 * die andere Richtung: So schickt die App einen Auftrag an den Einlieferer, ohne dass
 * der Server ihn lesen kann. (Und so tun es die Tests, wenn sie eine Einlieferung
 * nachstellen.)
 */
export function versiegle(data: Uint8Array, publicKey: string): string {
  return toB64(sodium.crypto_box_seal(data, fromB64(publicKey)));
}
